import React, { useMemo } from 'react';
import {
  IconClipboardType,
  IconCloseButton,
  IconEye,
  IconFlag,
  IconMark,
  IconStopwatch,
  OrangeButton,
  SmallButton,
} from 'components';
import { COLORS } from 'utils/colors';
import dayjs from 'dayjs';
import { CLIENT_STATUS, DATE_FORMAT } from 'api/constants';
import { getClientTaskProgressState } from 'utils/helpers';

export const SingleClientTaskRow = ({
  task,
  weekIndex,
  granted,
  clientStatus,
  lastAssignedWeek,
  lastAssignedAt,
}) => {
  const generatedDate = task.assigned_at ? dayjs(task.assigned_at) : null;
  const taskState = useMemo(
    () => getClientTaskProgressState(task, weekIndex, lastAssignedWeek, lastAssignedAt),
    [lastAssignedAt, lastAssignedWeek, task, weekIndex],
  );
  // const initialDate = dayjs(task.updated_at);

  const storeTaskButtons = useMemo(() => {
    if (task.completed) {
      return (
        <div className="flex items-center justify-end gap-x-6 w-full">
          <SmallButton
            icon={<IconEye className={'hover:text-darkGrey'} color={COLORS.blue} />}
            label="View"
            href={`/weekly-forms/${weekIndex}`}
          />
        </div>
      );
    } else if (task.task_id || (!task.task_id && weekIndex < lastAssignedWeek)) {
      return (
        <div className="flex items-center justify-end gap-x-6 w-full">
          <OrangeButton
            disabled={!task.task_id}
            href={`/weekly-forms/${weekIndex}`}
            label="Open"
            icon={<IconClipboardType className="min-w-6 min-h-6" />}
          />
        </div>
      );
    }
    return (
      <div className="flex items-center justify-end gap-x-6 w-full">
        <OrangeButton
          disabled={!task.task_id}
          href={`/weekly-forms/${weekIndex}`}
          label="Open"
          icon={<IconClipboardType className="min-w-6 min-h-6" />}
        />
      </div>
    );
  }, [lastAssignedWeek, task.completed, task.task_id, weekIndex]);

  if (clientStatus === CLIENT_STATUS.not_started || taskState === 'upcoming') {
    return (
      <div className="flex bg-lightGrey rounded-lg shadow-block-shadow h-[76px] overflow-hidden pointer-events-none">
        <div className={`flex items-center w-1/5  gap-x-4 px-5 bg-grey`}>
          <IconFlag color={COLORS.darkGrey} className="min-w-7" />
          <p className="text-24 font-normal text-darkGrey whitespace-nowrap">Week {weekIndex}</p>
        </div>
        <div className="w-4/5 flex items-center px-4 gap-x-8">
          <div className="w-1/4 flex items-center gap-x-1">
            <IconClipboardType />
            <p className="flex text-16 text-lightBlue">-</p>
          </div>
          <p className="text-14 font-medium w-1/4 text-lightBlue">-</p>
          <p className="text-14 font-medium w-1/4 text-lightBlue">-</p>
          <p className="text-14 text-lightBlue w-1/4">Upcoming</p>
          <div className="w-1/4 flex justify-end">
            <div className="flex items-center justify-end gap-x-6 w-full">
              <OrangeButton
                disabled
                href={`/weekly-forms/${weekIndex}`}
                label="Open"
                icon={<IconClipboardType className="min-w-6 min-h-6" />}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  const bgColor = () => {
    switch (taskState) {
      case 'completed':
        return 'bg-green';
      case 'pending':
        return 'bg-orange';
      case 'overdue':
        return 'bg-red';
      default:
        return 'bg-grey';
    }
  };
  const leftBg = bgColor();
  const leftIsGrey = leftBg === 'bg-grey';

  const iconType = () => {
    if (task.completed) return <IconMark color={COLORS.white} className="min-w-7" />;
    if (taskState === 'current')
      return (
        <IconStopwatch className="min-w-7" color={leftIsGrey ? COLORS.darkGrey : COLORS.white} />
      );
    if (lastAssignedWeek === weekIndex && clientStatus === CLIENT_STATUS.completed)
      return (
        <IconCloseButton
          color={leftIsGrey ? COLORS.darkGrey : COLORS.white}
          className="min-w-7 min-h-7"
        />
      );
    if (!task.completed) {
      return (
        <IconCloseButton
          color={leftIsGrey ? COLORS.darkGrey : COLORS.white}
          className="min-w-7 min-h-7"
        />
      );
    }
    return (
      <IconStopwatch className="min-w-7" color={leftIsGrey ? COLORS.darkGrey : COLORS.white} />
    );
  };

  return (
    <div className="flex bg-white rounded-lg shadow-block-shadow h-[76px] overflow-hidden">
      <div className={`flex items-center w-[20%] gap-x-4 px-5 ${bgColor()}`}>
        {iconType()}
        <p className={`text-24 font-normal whitespace-nowrap ${leftIsGrey ? 'text-darkGrey' : 'text-white'}`}>
          Week {weekIndex}
        </p>
      </div>
      <div className="w-4/5 flex items-center px-4 gap-x-8">
        <div className={`${task.task_id ? 'w-1/4' : 'w-2/4'} flex items-center gap-x-1`}>
          <IconClipboardType color={task.task_id ? COLORS.black : COLORS.lightBlue} />
          <p className="flex text-16 gap-x-1">
            {task.task_id ? (
              <span>
                <span className={`${task.completed ? 'text-green' : 'text-red'}`}>
                  {Number(task.completed)}
                </span>{' '}
                (1)
              </span>
            ) : (
              taskState === 'current' && (
                <span className="text-lightBlue text-sm">There are no form for this week yet</span>
              )
            )}
          </p>
        </div>
        <p className={`text-14 font-medium ${task.task_id ? 'w-1/4' : 'w-0 text-[0]'}`}>
          {weekIndex <= lastAssignedWeek ? granted : '-'}
        </p>
        <p className="text-14 font-medium w-1/4">
          {generatedDate ? generatedDate.format(DATE_FORMAT) : '-'}
        </p>
        <p className="text-14 font-medium w-1/4">
          {(() => {
            if (task.completed) return <span className="text-green">Completed</span>;
            if (taskState === 'current') return <span className="text-blue">Current</span>;
            if (!generatedDate) return <span className="text-lightBlue">-</span>;
            if (taskState === 'pending') {
              const now = dayjs();
              const deadline = dayjs(lastAssignedAt).add(7, 'day');
              const oneDay = 24 * 60 * 60 * 1000;
              const remaining = Math.max(
                0,
                Math.ceil((deadline.valueOf() - now.valueOf()) / oneDay),
              );
              return (
                <span className="text-orange flex items-center gap-x-2">
                  Pending <span className="text-lightBlue">({remaining} days left)</span>
                </span>
              );
            }
            return <span className="text-red">Overdue</span>;
          })()}
        </p>
        <div className="w-1/4 flex justify-end">{storeTaskButtons}</div>
      </div>
    </div>
  );
};
